import { Pipe, PipeTransform } from '@angular/core';
import { InstallerPassModel } from '@bo-client/shared/models/installerPassDto';
import {
  InstallerPassStatusEnum,
  InstallerPassStatusType
} from '@bo-client/technician-management/tm-user/tm-user.model';
import { StatusCatalogKey } from '@bo-client/shared/status/status.model';
import { StatusService } from '@bo-client/shared/status/status.service';
import { DatesService } from '@bo-client/shared/dates/dates.service';
import { TmUserService } from '@bo-client/technician-management/tm-user/tm-user.service';
import { AreaTypesModel } from '@bo-client/shared/models/areaTypes';

@Pipe({
  name: 'statusPassProgressPass'
})
export class StatusProgressPipe implements PipeTransform {
  transform(pass: InstallerPassModel) {
    if (!pass) {
      return 0;
    }
    const totalDocs: number = pass.answers.filter((xDoc) => xDoc.docType == 'doc').length;
    const loadedDocs: number = pass.answers.filter(
      (xDoc) => xDoc.attachments[0].url != null && xDoc.docType == 'doc'
    ).length;
    if (loadedDocs == 0) {
      return 0;
    }
    return Math.round((loadedDocs / totalDocs) * 100);
  }
}

@Pipe({
  name: 'statusPassCss',
  pure: false
})
export class StatusPassCssPipe implements PipeTransform {
  constructor(private statusService: StatusService) {
    // Empty
  }

  transform(passStatus: InstallerPassStatusType) {
    return this.statusService.color('installerPassList', passStatus);
  }
}

@Pipe({
  name: 'passStatusName',
  pure: false
})
export class PassStatusDisplayPipe implements PipeTransform {
  constructor(
    private datesService: DatesService,
    private tmUserService: TmUserService,
    private statusService: StatusService
  ) {
    // Empty
  }

  transform(passStatus: InstallerPassStatusType, datePassExpired?: Date): string {
    if (!passStatus) {
      return '-';
    }
    const status = this.statusService.find('installerPassList', passStatus);
    if (status) {
      if (!!datePassExpired && !this.datesService.dateIsExpired(datePassExpired)) {
        const leftDays = this.tmUserService.calcDaysToRenew(datePassExpired);
        if (leftDays < 60) {
          const plural = leftDays > 1 ? 'en' : '';
          return 'Läuft in ' + leftDays + ' Tag' + plural + ' ab.';
        }
      }
      return status.displayName;
    } else {
      return '-';
    }
  }
}

@Pipe({
  name: 'passExpired'
})
export class PassExpiredPipe implements PipeTransform {
  constructor(
    private datesService: DatesService,
    private tmUserService: TmUserService,
    private statusService: StatusService
  ) {
    // Empty
  }

  transform(pass: InstallerPassModel, areaTypeList: AreaTypesModel[], daysBefore?: number) {
    if (!pass) {
      return null;
    }
    const knownStatus = !!this.statusService.find('installerPassList', pass.status);
    const textToDisplay =
      'Der ' +
      areaTypeList.find((y) => y.id == pass.idarea).description +
      '-Ausweis Nummer: ' +
      pass.passNummer;
    if (knownStatus) {
      if (pass.status === InstallerPassStatusEnum.Expired) {
        return textToDisplay + ' ist abgelaufen';
      }
      if (
        pass.status === InstallerPassStatusEnum.Valid &&
        !!pass.expirationdate &&
        !this.datesService.dateIsExpired(pass.expirationdate)
      ) {
        const leftDays = this.tmUserService.calcDaysToRenew(pass.expirationdate);
        if (leftDays < daysBefore) {
          const plural = leftDays > 1 ? 'en' : '';
          return textToDisplay + ' läuft in ' + leftDays + ' Tag' + plural + ' ab.';
        }
      }
      return null;
    } else {
      return null;
    }
  }
}

@Pipe({
  name: 'filterExpirePass'
})
export class FilterExpirePassPipe implements PipeTransform {
  constructor(private datesService: DatesService, private tmUserService: TmUserService) {
    // Empty
  }

  transform(passList: InstallerPassModel[], daysBefore?: number) {
    if (!passList) {
      return [];
    }

    return passList.filter(
      (x) =>
        (x.status == InstallerPassStatusEnum.Valid || x.status == InstallerPassStatusEnum.Expired) &&
        (this.datesService.dateIsExpired(x.expirationdate) ||
          this.tmUserService.calcDaysToRenew(x.expirationdate) < daysBefore)
    );
  }
}

/**
 * Bezeichnung (`type` ≠ `'css'`) bzw. Farbe (`type = 'css'`) eines Vorgangsstatus. Mit `module` wird im
 * Katalog dieses Moduls nachgeschlagen, sonst in allen Vorgangs-Katalogen (erster Treffer).
 */
@Pipe({
  name: 'requestStatusNameAndCss',
  pure: false
})
export class RequestStatusPipe implements PipeTransform {
  constructor(private statusService: StatusService) {
    // Empty
  }

  transform(status: string, type: string, module?: StatusCatalogKey) {
    if (!status) {
      return type == 'css' ? '#c9c9c9' : '-';
    }
    const option = module ? this.statusService.find(module, status) : this.statusService.findAcross(status);
    if (option) {
      return type == 'css' ? option.cssColor : option.displayName;
    } else {
      return type == 'css' ? '#c9c9c9' : status;
    }
  }
}

/** Bezeichnung eines Vorgangsstatus; ohne `module` modulübergreifend (erster Treffer). */
@Pipe({
  name: 'statusDisplayName',
  pure: false
})
export class StatusDisplayNamePipe implements PipeTransform {
  constructor(private statusService: StatusService) {
    // Empty
  }

  transform(status: string, module?: StatusCatalogKey) {
    const option = module ? this.statusService.find(module, status) : this.statusService.findAcross(status);
    return option ? option.displayName : status;
  }
}
